/**
 * Which questions let a file attached to a thread reply become part of the
 * vendor's answer (Replaces / New), rather than staying a loose attachment.
 * Single source of truth for the API gate (apps/api commentAttachment) and the
 * reply composer (packages/ui ReplyComposer call sites), so the server never
 * rejects a file the composer offered, or stores loose a file it promised to
 * promote. Spec: docs/superpowers/specs/2026-10-16-reply-attachment-upload-questions-design.md
 *
 * The rule is "has a plain upload box ⇔ a reply can promote". It mirrors the
 * vendor's FilesEditor render condition in apps/vendor/src/company/Questionnaire.tsx
 * (`allow_upload || requires_upload || upload_label`, by truthiness, so an empty
 * label is no label), with three deliberate differences:
 *
 * - `document_upload` is always true, including `adoptable_doc_id` rows that
 *   render no box: that is how reply attachments on it already behaved.
 * - `multiselect_uploads` is false: its files are per option (`option_key`),
 *   which a reply cannot express yet. Deferred to a separate change.
 * - `informational` and `image_info` are false: the questionnaire returns their
 *   cards before the FilesEditor branch, so a stray flag shows no box.
 *
 * `signature` and `document_adopt` are NOT excluded: they still reach the
 * FilesEditor branch, so a stray flag does show a box there. The predicate has
 * to match what the vendor sees, not the builder's `suppressUpload`.
 *
 * Callers must pass the effective (frozen) question, never the live row.
 */
export type ReplyAttachmentQuestion = {
  type: string;
  allow_upload?: boolean | null;
  requires_upload?: boolean | null;
  upload_label?: string | null;
};

const NEVER_PROMOTES = new Set(['multiselect_uploads', 'informational', 'image_info']);

/** "Has an upload box ⇔ reply can promote": equals the vendor FilesEditor render condition minus the per-option branch. */
export function replyAttachmentCanPromote(q: ReplyAttachmentQuestion): boolean {
  if (q.type === 'document_upload') return true;
  if (NEVER_PROMOTES.has(q.type)) return false;
  return !!(q.allow_upload || q.requires_upload || q.upload_label);
}

/** Only document_upload's upload box offers an expiry date, so only its reply attachments may carry one. */
export function replyAttachmentOffersExpiry(q: ReplyAttachmentQuestion): boolean {
  return q.type === 'document_upload';
}

export type ReplyFileProjectionInput = {
  /** The question's live (non-superseded) answer file ids, in display order. */
  liveIds: string[];
  removesFileIds: string[];
  replacesFileIds: string[];
  /** Reply attachments that become answer files (Replaces / New). */
  newAnswerFileCount: number;
  allowMultipleFiles: boolean;
};

/**
 * The question's live answer files once the reply lands, for feeding
 * owesFileAfter before anything is written. New files get synthetic ids
 * (`new:0`, `new:1`, ...); only the count matters to the gate.
 *
 * A single-file question supersedes every live file when a new answer file
 * arrives, so it projects to the new files alone. Otherwise: the live set, in
 * its order, minus removals and Replace targets, plus the new files.
 */
export function projectLiveFilesAfterReply(args: ReplyFileProjectionInput): { id: string }[] {
  const incoming = Array.from({ length: args.newAnswerFileCount }, (_, i) => ({ id: `new:${i}` }));
  if (!args.allowMultipleFiles && args.newAnswerFileCount > 0) return incoming;
  const dropped = new Set([...args.removesFileIds, ...args.replacesFileIds]);
  const kept = args.liveIds.filter((id) => !dropped.has(id)).map((id) => ({ id }));
  return [...kept, ...incoming];
}
